import { HOST, resolveAnchor, type AnchorSpec } from '../engine/anchors';
import { pressWhenReady, waitForAnchor, waitUntil } from './formFill';

const OPEN_TIMEOUT_MS = 2500;

const CLOSE_TIMEOUT_MS = 900;

const CLOSE_ATTEMPTS = 3;

export const POINTER_OUT_EVENTS = ['pointerout', 'pointerleave', 'mouseout', 'mouseleave'] as const;

export interface HoverMenu {
  name: string;
  trigger: string;
  panel: AnchorSpec;
}

export function isMenuOpen(menu: HoverMenu): boolean {
  return Boolean(resolveAnchor(menu.panel));
}

export async function openHoverMenu(menu: HoverMenu): Promise<boolean> {
  if (isMenuOpen(menu)) return true;
  if (!(await pressWhenReady({ selector: menu.trigger }))) return false;
  return waitForAnchor(menu.panel, OPEN_TIMEOUT_MS);
}

export async function closeHoverMenu(menu: HoverMenu): Promise<boolean> {
  for (let attempt = 0; attempt < CLOSE_ATTEMPTS && isMenuOpen(menu); attempt += 1) {
    document.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
      }),
    );
    await waitUntil(() => !isMenuOpen(menu), CLOSE_TIMEOUT_MS);
  }

  if (isMenuOpen(menu)) {
    console.warn(
      `[tour-guide] the ${menu.name} menu is still open after ` +
        `${CLOSE_ATTEMPTS} attempts to dismiss it. The tour will resume behind it.`,
    );
    return false;
  }
  return true;
}

export function shouldHoldPointerOut(
  target: Element | null,
  selectors: readonly string[],
): boolean {
  if (!target || selectors.length === 0) return false;
  try {
    return Boolean(target.closest(selectors.join(',')));
  } catch {
    return false;
  }
}

export function holdMenuOpen(menu: HoverMenu): () => void {
  const owned = [menu.trigger, HOST.popperWrapper];

  const swallow = (event: Event) => {
    if (!event.isTrusted) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!shouldHoldPointerOut(target, owned)) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
  };

  for (const type of POINTER_OUT_EVENTS) {
    document.addEventListener(type, swallow, true);
  }

  return () => {
    for (const type of POINTER_OUT_EVENTS) {
      document.removeEventListener(type, swallow, true);
    }
  };
}
